import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';

import { IconComponent } from '@repo/ui-icons';
import IconChevronLeftXL from '@repo/ui-icons/IconChevronLeftXL';
import IconClose from '@repo/ui-icons/IconClose';
import cn from 'classnames';
import { Drawer as VaulDrawer } from 'vaul';

import { utilityClasses } from '../../../css/utils';
import { type BaseComponentProps } from '../../../types/common';
import { Button } from '../../core/Button';
import { H4 } from '../../core/Typography';

import styles from './Drawer.module.css';
import { DrawerContext, useDrawerContext } from './DrawerContext';

export enum DrawerSnapPoints {
  CLOSED = 1e-6, // Drawer does not support 0 as a snap point, so we use a small value instead
  HALF_OPENED = 0.6,
  FULL_OPENED = 1,
}

export type DrawerTriggerProps = React.PropsWithChildren<BaseComponentProps>;

const DrawerTrigger: React.FC<DrawerTriggerProps> = ({ children }) => {
  return <VaulDrawer.Trigger asChild>{children}</VaulDrawer.Trigger>;
};

interface DrawerHeaderOwnProps extends BaseComponentProps {
  /* The title of the Drawer */
  title?: string;
  /* Sets the padding of the header */
  padding?: React.CSSProperties['padding'];
  /* The onClick of the back button */
  onBackClick?: () => void;
  /* The icon that will be shown in the header */
  icon?: IconComponent;
}

export type DrawerHeaderProps = React.PropsWithChildren<DrawerHeaderOwnProps>;

const DrawerHeader: React.FC<DrawerHeaderProps> = ({
  title,
  padding = '0',
  icon: Icon,
  onBackClick,
}) => {
  const hasBackButton = !!onBackClick;
  const hasIcon = !!Icon;

  const drawerHeaderClassName = cn(styles['drawer-header'], {
    [styles['with-backbutton']]: hasBackButton,
    [styles['with-icon']]: hasIcon,
  });

  return (
    <div className={drawerHeaderClassName} style={{ padding }}>
      {onBackClick && (
        <Button
          ariaLabel="Back Button"
          className={styles['back-button']}
          dataTestId="drawer-back-button"
          leftIcon={IconChevronLeftXL}
          name="drawerBackButton"
          size="xs"
          variant="ghost"
          onClick={onBackClick}
        />
      )}

      {hasIcon && (
        <span className={styles.icon}>
          <Icon color="var(--color-text)" size={18} />
        </span>
      )}

      {title && (
        <span className={styles.title}>
          <VaulDrawer.Title asChild>
            <H4 textOverflowEllipsis>{title}</H4>
          </VaulDrawer.Title>
        </span>
      )}

      <VaulDrawer.Close asChild>
        <Button
          ariaLabel="Close Button"
          className={styles['close-button']}
          dataTestId="drawer-close-button"
          leftIcon={IconClose}
          name="drawerCloseButton"
          size="s"
          variant="ghost"
        />
      </VaulDrawer.Close>
    </div>
  );
};

const DrawerFooter: React.FC<React.PropsWithChildren<BaseComponentProps>> = ({
  children,
}) => {
  return <div className={styles['drawer-footer']}>{children}</div>;
};

interface DrawerContentOwnProps extends BaseComponentProps {
  // Sets the gap of the component that wraps the content
  gap?: React.CSSProperties['gap'];
  /* Sets the padding of the content */
  padding?: React.CSSProperties['padding'];
  // Ref that will be passed to the content wrapper
  contentWrapperRef?: React.Ref<HTMLDivElement>;
  // Controls if the content should stay mounted when the drawer is closed.
  // Useful for preserving the drawer state on re-opening.
  forceMount?: true | undefined;
}

export type DrawerContentProps = React.PropsWithChildren<DrawerContentOwnProps>;

const DrawerContent: React.FC<DrawerContentProps> = ({
  children,
  gap = 'initial',
  padding = 'var(--spacing-8) var(--spacing-16)',
  contentWrapperRef,
  forceMount,
}) => {
  const { activeSnapPoint, isOpen } = useDrawerContext();

  let headerChild: React.ReactNode | undefined = undefined;
  let footerChild: React.ReactNode | undefined = undefined;
  const otherChildren: React.ReactNode[] = [];

  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child) && child.type === DrawerHeader) {
      headerChild = child;
    } else if (React.isValidElement(child) && child.type === DrawerFooter) {
      footerChild = child;
    } else {
      otherChildren.push(child);
    }
  });

  const drawerInnerContentClassName = cn(styles['scrollable-content'], {
    [styles['full-opened']]: activeSnapPoint === DrawerSnapPoints.FULL_OPENED,
    [styles['footer-visible']]: !!footerChild,
  });

  return (
    <VaulDrawer.Portal forceMount={forceMount}>
      <VaulDrawer.Content
        aria-describedby={undefined} // We are not supporting description yet
        className={cn(styles.content, {
          [utilityClasses['hidden']]: !isOpen && forceMount,
        })}
        style={{ padding }}
      >
        <VaulDrawer.Handle className={styles['drawer-handle']} />

        {headerChild}

        {/* TODO: replace with ScrollableArea */}
        <div
          ref={contentWrapperRef}
          className={drawerInnerContentClassName}
          style={{ gap }}
        >
          {otherChildren}
        </div>

        {footerChild}
      </VaulDrawer.Content>
    </VaulDrawer.Portal>
  );
};

interface DrawerRootOwnProps extends BaseComponentProps {
  /** When false it allows to interact with elements outside of the drawer without closing it */
  modal?: boolean;
  /** It opens the drawer when true */
  open?: boolean;
  /** Callback that is called when the drawer is opened or closed */
  onOpenChange?: (open: boolean) => void;
  /** it sets the default snap point of the Drawer */
  defaultSnapPoint?: DrawerSnapPoints;
  /** Callback that is called when the snap point of the drawer changes */
  onSnapPointChange?: (snapPoint: DrawerSnapPoints) => void;
  /** When true it prevents the background of the page from scrolling when the drawer is open */
  enableBackgroundScrolling?: boolean;
  /** The snap points of the drawer */
  snapPoints?: DrawerSnapPoints[];
}

export type DrawerRootProps = React.PropsWithChildren<DrawerRootOwnProps>;

const DrawerRoot: React.FC<DrawerRootProps> = ({
  children,
  open,
  onOpenChange,
  onSnapPointChange,
  modal = false,
  defaultSnapPoint = DrawerSnapPoints.HALF_OPENED,
  enableBackgroundScrolling = true,
  snapPoints = [DrawerSnapPoints.HALF_OPENED, DrawerSnapPoints.FULL_OPENED],
}) => {
  const [settledOpenState, setSettledOpenState] = useState(open ?? false);
  const [activeSnapPoint, setActiveSnapPoint] = useState<
    number | string | null
  >(defaultSnapPoint);

  useEffect(() => {
    if (open) {
      setSettledOpenState(true);
    }
  }, [open]);

  const disableScrollingInBackground = useCallback((): void => {
    if (!enableBackgroundScrolling) {
      document.body.style.overflow = 'hidden';
    }
  }, [enableBackgroundScrolling]);

  const enableScrollingInBackground = useCallback((): void => {
    document.body.style.overflow = 'unset';
  }, []);

  // Hack to remove focus trap
  // It's an open issue in the vaul library that comes from radix-ui dialog
  // https://github.com/emilkowalski/vaul/issues/497
  useLayoutEffect(() => {
    document.addEventListener('focusin', (e) => e.stopImmediatePropagation());
    document.addEventListener('focusout', (e) => e.stopImmediatePropagation());
  }, []);

  useEffect(() => {
    setActiveSnapPoint(defaultSnapPoint);
  }, [defaultSnapPoint]);

  /**
   * By default, the drawer fully closes only when the content is unmounted and this causes the drawer
   * to show at the wrong position when Drawer.Content shouldForceMount is true and
   * drawer is re-opened after being closed with dragging.
   * Setting the active snap point to closed forces the drawer to close while the content is still mounted.
   *  */
  useEffect(() => {
    setActiveSnapPoint(
      settledOpenState ? defaultSnapPoint : DrawerSnapPoints.CLOSED
    );
  }, [settledOpenState, defaultSnapPoint]);

  useEffect(() => {
    if (open) {
      disableScrollingInBackground();
    } else {
      enableScrollingInBackground();
    }
  }, [disableScrollingInBackground, enableScrollingInBackground, open]);

  const handleSnapPointChange = (
    newSnapPoint: string | number | null
  ): void => {
    if (newSnapPoint) {
      setActiveSnapPoint(newSnapPoint);
      onSnapPointChange?.(newSnapPoint as DrawerSnapPoints);
    }
  };

  const handleAnimationEnd = (isOpen: boolean): void => {
    setSettledOpenState(isOpen);
  };

  return (
    <DrawerContext.Provider value={{ activeSnapPoint, isOpen: open ?? false }}>
      <VaulDrawer.Root
        handleOnly
        activeSnapPoint={activeSnapPoint}
        modal={modal}
        open={open}
        setActiveSnapPoint={handleSnapPointChange}
        snapPoints={[DrawerSnapPoints.CLOSED, ...snapPoints]}
        onAnimationEnd={handleAnimationEnd}
        onOpenChange={onOpenChange}
      >
        {children}
      </VaulDrawer.Root>
    </DrawerContext.Provider>
  );
};

export const Drawer = {
  Root: DrawerRoot,
  Trigger: DrawerTrigger,
  Header: DrawerHeader,
  Content: DrawerContent,
  Footer: DrawerFooter,
};
